import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import type { LocalFood } from '../../db/local.js';
import { deleteFood, listFoods } from '../../db/nutrition.js';
import { RecipeLibrary } from '../recipes/RecipeLibrary.js';
import { formatNumber } from '../training/SessionScreen.js';
import { FoodForm } from './FoodForm.js';
import { SupplementLibrary } from './SupplementLibrary.js';
import { useIsChild } from '../../hooks/useModules.js';

type LibraryTab = 'recipes' | 'foods' | 'supplements';

/**
 * Lebensmittel, Gerichte und (seit 16.10.2026) Supplements pflegen.
 *
 * Zwei Ebenen, bewusst getrennt: **Lebensmittel** tragen Nährwerte je 100 g von
 * der Packung, **Gerichte** sind das, was auf dem Teller landet. Ein Gericht
 * darf seine Werte direkt tragen (Schätzung, Restaurant) oder aus Zutaten
 * rechnen — die App zeigt an, welcher Fall vorliegt, statt beides gleich
 * aussehen zu lassen.
 */
export function FoodLibrary({
  initialTab = 'recipes',
}: {
  initialTab?: LibraryTab;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Supplements nie für Kinder — auch nicht für ein Teen mit Ernährung
  // (27.10.2026): Die Dosen im Fachkonzept gelten Erwachsenen.
  const child = useIsChild();
  const [tab, setTab] = useState<LibraryTab>(
    child && initialTab === 'supplements' ? 'recipes' : initialTab,
  );

  return (
    <section aria-labelledby="library-heading" className="flex flex-col gap-4">
      <h2 id="library-heading" className="text-lg font-semibold">
        {t('nutrition.library.heading')}
      </h2>

      <div className="flex gap-1">
        {(child
          ? (['recipes', 'foods'] as const)
          : (['recipes', 'foods', 'supplements'] as const)
        ).map((key) => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => setTab(key)}
            className={`rounded-lg border px-3 py-2 text-sm ${
              tab === key ? 'border-accent text-accent' : 'border-line text-ink-muted'
            }`}
          >
            {t(`nutrition.library.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'recipes' ? <RecipeLibrary /> : null}
      {tab === 'foods' ? <FoodList /> : null}
      {tab === 'supplements' && !child ? <SupplementLibrary /> : null}
    </section>
  );
}

/**
 * Lebensmittel als schmale Liste (25.10.2026, Timo: „braucht eine schönere
 * Übersicht, die Knöpfe nehmen zu viel Platz weg"): Name, Marke und die vier
 * Werte je 100 g in einer Zeile; Tippen klappt Bearbeiten und Löschen auf.
 * Fehlt die Quelle, steht das in der Zeile — Nährwerte ohne Quelle sähen
 * sonst aus wie geprüft.
 */
function FoodList(): React.JSX.Element {
  const { t } = useTranslation();
  const searchId = useId();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = needle
      ? foods.filter((food) => `${food.name} ${food.brand ?? ''}`.toLowerCase().includes(needle))
      : foods;
    return [...matches].sort((a, b) => a.name.localeCompare(b.name));
  }, [foods, query]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-ink-muted">{t('nutrition.library.foodsIntro')}</p>
        <button
          type="button"
          onClick={() => setCreating((value) => !value)}
          aria-expanded={creating}
          className="shrink-0 kw-btn kw-btn-small"
        >
          {t('nutrition.library.newFood')}
        </button>
      </div>

      {creating ? <FoodForm onDone={() => setCreating(false)} /> : null}

      {foods.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('nutrition.library.noFoods')}</p>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <label htmlFor={searchId} className="text-xs text-ink-muted">
              {t('nutrition.library.searchFoods')}
            </label>
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="kw-field"
            />
          </div>
          {shown.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('nutrition.library.noFoodMatch')}</p>
          ) : (
            <ul className="kw-tile flex flex-col divide-y divide-line p-0">
              {shown.map((food) => {
                const expanded = open === food.id;
                return (
                  <li key={food.id}>
                    <button
                      type="button"
                      onClick={() => setOpen(expanded ? null : food.id)}
                      aria-expanded={expanded}
                      className="flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left hover:bg-surface-raised focus-visible:bg-surface-raised"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {food.name}
                          {food.brand ? (
                            <span className="font-normal text-ink-muted"> · {food.brand}</span>
                          ) : null}
                        </span>
                        <span className="block truncate text-xs text-ink-muted">
                          {t('nutrition.library.foodLine', {
                            kcal: Math.round(food.kcalPer100g),
                            protein: formatGrams(food.proteinPer100g),
                            carbs: formatGrams(food.carbsPer100g),
                            fat: formatGrams(food.fatPer100g),
                          })}
                          {food.sourceNote ? null : (
                            <span className="text-warn"> · {t('nutrition.food.noSource')}</span>
                          )}
                        </span>
                      </span>
                      <span aria-hidden="true" className="shrink-0 text-ink-muted">
                        {expanded ? '▾' : '›'}
                      </span>
                    </button>

                    {expanded ? (
                      <div className="flex flex-col gap-3 px-4 pb-4">
                        <FoodForm food={food} onDone={() => setOpen(null)} />
                        <button
                          type="button"
                          onClick={() => void deleteFood(food.id, db)}
                          aria-label={t('nutrition.library.deleteNamed', { name: food.name })}
                          className="kw-btn kw-btn-quiet kw-btn-small self-start px-3 hover:border-danger hover:text-danger"
                        >
                          {t('nutrition.library.delete')}
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/** Gramm mit höchstens einer Nachkommastelle — „0,4 g" bleibt sichtbar. */
function formatGrams(value: number): string {
  return formatNumber(Math.round(value * 10) / 10);
}
